/**
 * Canonical ops board — TARGET architecture (not the old split-store map).
 *
 * ```
 * Writers (Megan / Classic / Grok / desk / SimConnect phase machine)
 *        │
 *        ▼
 * Canonical Mission ticket  (id, CEL####/F, departureIso, eteMin, status,
 *                            lifecycle, origin/dest, aircraftId)
 *        │
 *        ├── Assignments  (crew ↔ ticket)
 *        ├── Inbox RFQ    (acceptedMissionId → live ticket STD/status)
 *        └── Flight Log   (projection / history of the same tripId)
 *        │
 *        ▼
 * Readers: Inbox · Trips · Flight plans · Flight Log · Dashboard · Map
 *          · status bar · companion  — all call these selectors.
 * ```
 *
 * Current (buggy) vs target: logs used to be a silo that only synced identity
 * or cancel. Target: every mutation writes the mission, then projects logs
 * and every selector rereads the same ticket.
 */
import { coerceClockDate, formatHalifaxDateTime, halifaxParts } from "./clock";
import { displayFlightNumber } from "./flightNumber";
import { ticketStaMs, ticketStdMs } from "./flightOpsStatus";
import { assignmentForMission, missionsForCrew, type TripAssignment } from "./assignment";
import type {
  CharterRequest,
  FlightLogStatus,
  Mission,
  MissionStatus,
  TicketLifecycle,
} from "./types";

export const TICKET_LIFECYCLES: readonly TicketLifecycle[] = [
  "scheduled",
  "accepted",
  "active",
  "taxi",
  "airborne",
  "diverting",
  "arrived",
  "diverted",
  "completed",
  "cancelled",
];

export const TICKET_LIFECYCLE_LABEL: Record<TicketLifecycle, string> = {
  scheduled: "Scheduled",
  accepted: "Accepted",
  active: "Active",
  taxi: "Taxi",
  airborne: "Airborne",
  diverting: "Diverting",
  arrived: "Arrived",
  diverted: "Diverted",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const LOG_STATUS_FROM_LIFECYCLE: Record<TicketLifecycle, FlightLogStatus> = {
  scheduled: "filed",
  accepted: "accepted",
  active: "accepted",
  taxi: "taxi",
  airborne: "airborne",
  diverting: "diverting",
  arrived: "arrived",
  diverted: "diverted",
  completed: "complete",
  cancelled: "cancelled",
};

export type OpsBoardGroup = "active" | "upcoming" | "completed" | "cancelled";

export const OPS_BOARD_GROUP_LABEL: Record<OpsBoardGroup, string> = {
  active: "Active",
  upcoming: "Upcoming",
  completed: "Completed",
  cancelled: "Cancelled",
};

const LIVE_LIFECYCLES: ReadonlySet<TicketLifecycle> = new Set([
  "taxi",
  "airborne",
  "diverting",
  "arrived",
  "diverted",
]);

export function isTicketLifecycle(value: unknown): value is TicketLifecycle {
  return typeof value === "string" && (TICKET_LIFECYCLES as readonly string[]).includes(value);
}

/** Taxi / airborne / divert / arrive — only live sim (or a prior sim write) may own these. */
export function isSimTruthLifecycle(life?: TicketLifecycle | null): boolean {
  return life === "taxi" || life === "airborne" || life === "diverting" || life === "arrived" || life === "diverted";
}

/** Human Activate / Mark Active — Active/Armed, never Airborne. */
export function lifecycleForHumanArm(mission: Pick<Mission, "lifecycle">): TicketLifecycle {
  return isSimTruthLifecycle(mission.lifecycle) ? mission.lifecycle! : "active";
}

export function isOpenOpsTicket(status: MissionStatus): boolean {
  return status === "pending" || status === "in-progress";
}

function halifaxDayKey(date: Date): string {
  const p = halifaxParts(date);
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/**
 * Shared live / today list — Dispatch Today, Pilot Trips, Flight Plans.
 * Open tickets (including airborne ferries) stay regardless of STD day.
 * Multi-day completed drop off Plans / thin Pilot lists. Trip Log is history.
 */
export function filterLiveTodayTickets(missions: readonly Mission[], now: Date = new Date()): Mission[] {
  const today = halifaxDayKey(now);
  return missions.filter((m) => {
    if (isOpenOpsTicket(m.status)) return true;
    const dep = coerceClockDate(m.departureIso);
    if (!dep) return false;
    const day = halifaxDayKey(dep);
    if (day === today) return true;
    return m.status === "cancelled" && day >= today;
  });
}

export function ticketLifecycle(
  mission: Pick<Mission, "status" | "lifecycle" | "activeBoundAt">,
  assignment?: Pick<TripAssignment, "status"> | null,
): TicketLifecycle {
  if (mission.status === "cancelled") return "cancelled";
  if (mission.status === "completed") return "completed";
  if (isTicketLifecycle(mission.lifecycle) && mission.lifecycle !== "cancelled" && mission.lifecycle !== "completed") {
    return mission.lifecycle;
  }
  // in-progress without a stored phase is armed / opened for ops — never invent Airborne.
  if (mission.status === "in-progress") return mission.activeBoundAt ? "active" : "accepted";
  if (assignment?.status === "accepted") return "accepted";
  return "scheduled";
}

export function hydrateTicketLifecycle(mission: Mission, assignment?: Pick<TripAssignment, "status"> | null): Mission {
  const lifecycle = ticketLifecycle(mission, assignment);
  return mission.lifecycle === lifecycle ? mission : { ...mission, lifecycle };
}

export function hydrateBoardLifecycles(missions: Mission[], assignments: TripAssignment[] = []): Mission[] {
  return missions.map((m) => hydrateTicketLifecycle(m, assignmentForMission(assignments, m.id)));
}

function lifecycleRank(mission: Mission): number {
  const life = ticketLifecycle(mission);
  if (LIVE_LIFECYCLES.has(life) || mission.status === "in-progress") return 0;
  if (mission.status === "pending") return 1;
  if (mission.status === "completed" || life === "completed") return 2;
  return 3;
}

/** Next / airborne on top, later below, completed then cancelled — ops STD, not created-at. */
export function compareOpsTickets(a: Mission, b: Mission): number {
  const rank = lifecycleRank(a) - lifecycleRank(b);
  if (rank !== 0) return rank;
  const std = (ticketStdMs(a) ?? 0) - (ticketStdMs(b) ?? 0);
  if (std !== 0) return std;
  return a.id.localeCompare(b.id);
}

export function sortOpsBoard(missions: readonly Mission[]): Mission[] {
  return [...missions].sort(compareOpsTickets);
}

export function opsBoardGroup(mission: Mission): OpsBoardGroup {
  const life = ticketLifecycle(mission);
  if (life === "cancelled" || mission.status === "cancelled") return "cancelled";
  if (life === "completed" || mission.status === "completed") return "completed";
  if (LIVE_LIFECYCLES.has(life) || mission.status === "in-progress") return "active";
  return "upcoming";
}

export function groupOpsBoard(missions: readonly Mission[]): Record<OpsBoardGroup, Mission[]> {
  const grouped: Record<OpsBoardGroup, Mission[]> = {
    active: [],
    upcoming: [],
    completed: [],
    cancelled: [],
  };
  for (const row of sortOpsBoard(missions)) {
    grouped[opsBoardGroup(row)].push(row);
  }
  return grouped;
}

export type PickCurrentTicketInput = {
  missions: readonly Mission[];
  assignments?: TripAssignment[];
  crewId?: string;
};

/**
 * Current live ticket for tracker + companion + Flight plans next-up.
 * Keys off ticket identity + ops STD + in-progress lifecycle.
 * Does not guess from SI / SimConnect route.
 */
export function pickCurrentTicket(input: PickCurrentTicketInput): Mission | null {
  const scoped =
    input.crewId && input.assignments
      ? missionsForCrew([...input.missions], input.assignments, input.crewId)
      : [...input.missions];
  const open = scoped.filter((m) => isOpenOpsTicket(m.status));
  if (!open.length) return null;
  const live = open.filter((m) => lifecycleRank(m) === 0);
  const pool = live.length ? live : open;
  return sortOpsBoard(pool)[0] ?? null;
}

export type InboxTicketLink = {
  missionId: string;
  flightNumber: string;
  departureIso: string;
  lifecycle: TicketLifecycle;
  originIcao: string;
  destIcao: string;
};

/** After assign: Inbox shows On board → CEL#### with the **live ticket** STD. */
export function inboxTicketLink(
  request: Pick<CharterRequest, "id" | "acceptedMissionId">,
  missions: readonly Mission[],
): InboxTicketLink | null {
  const mission =
    missions.find((m) => request.acceptedMissionId && m.id === request.acceptedMissionId) ??
    missions.find((m) => m.inboxRequestId === request.id && m.flightType !== "ferry" && m.status !== "cancelled");
  if (!mission) return null;
  return {
    missionId: mission.id,
    flightNumber: displayFlightNumber(mission) || mission.callsign,
    departureIso: mission.departureIso,
    lifecycle: ticketLifecycle(mission),
    originIcao: mission.originIcao,
    destIcao: mission.destIcao,
  };
}

export function formatTicketStd(mission: Pick<Mission, "departureIso">): string {
  const ms = ticketStdMs(mission);
  if (ms == null) return "STD —";
  return `STD ${formatHalifaxDateTime(new Date(ms))}`;
}

export function formatTicketSta(mission: Pick<Mission, "departureIso" | "eteMin">): string {
  const ms = ticketStaMs(mission);
  if (ms == null) return "STA —";
  return `STA ${formatHalifaxDateTime(new Date(ms))}`;
}

export function ticketDisplayLabel(mission: Mission, assignment?: Pick<TripAssignment, "status"> | null): string {
  return TICKET_LIFECYCLE_LABEL[ticketLifecycle(mission, assignment)];
}

export type SelectorSnap = {
  currentId: string | null;
  currentStd: string | null;
  boardIds: string[];
  boardStds: string[];
  inboxStd: string | null;
  inboxFlight: string | null;
};

/** One snapshot every surface can assert after a mutation. */
export function opsSelectorSnap(input: {
  missions: readonly Mission[];
  assignments?: TripAssignment[];
  inbox?: ReadonlyArray<Pick<CharterRequest, "id" | "acceptedMissionId">>;
  crewId?: string;
  requestId?: string;
}): SelectorSnap {
  const current = pickCurrentTicket(input);
  const board = sortOpsBoard(input.missions);
  const link =
    input.requestId && input.inbox
      ? inboxTicketLink(input.inbox.find((r) => r.id === input.requestId) ?? { id: input.requestId }, input.missions)
      : null;
  return {
    currentId: current?.id ?? null,
    currentStd: current?.departureIso ?? null,
    boardIds: board.map((m) => m.id),
    boardStds: board.map((m) => m.departureIso),
    inboxStd: link?.departureIso ?? null,
    inboxFlight: link?.flightNumber ?? null,
  };
}
